/**
 * COMMERCE-WORKSPACE-FRAMEWORK-000001 — connection list + URL bootstrap for commerce desks.
 * Prefer Global Filter Engine connectionId (GLOBAL-FILTER-ENGINE-000001).
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { amazonConnectApi, type SellerConnection } from '../api/amazonconnect';
import { useGlobalFilterOptional } from '../global-filter';

export function useCommerceConnections() {
  const [params] = useSearchParams();
  const gfe = useGlobalFilterOptional();
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [localId, setLocalId] = useState('');

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const fromGfe = gfe?.context.connectionId || '';
        const urlConn = params.get('connection') || params.get('gfe_conn') || '';
        const preferred = fromGfe || urlConn;
        const pick =
          preferred && c.some((x) => x.id === preferred) ? preferred : c[0]?.id || '';
        setLocalId((prev) => prev || pick);
        if (pick && gfe && gfe.context.connectionId !== pick) {
          gfe.setFilters({
            connectionId: pick,
            sellerLabel:
              c.find((x) => x.id === pick)?.seller_ref || gfe.context.sellerLabel,
          });
        }
      })
      .catch(() => setConnections([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once + gfe/params seed
  }, [params]);

  const connectionId = gfe?.context.connectionId || localId;
  const setConnectionId = (id: string) => {
    setLocalId(id);
    const conn = connections.find((x) => x.id === id);
    gfe?.setFilters({
      connectionId: id || null,
      sellerLabel: conn?.seller_ref || gfe.context.sellerLabel,
      marketplace: conn?.marketplace_ids?.[0] || gfe.context.marketplace,
    });
  };

  const connection = connections.find((c) => c.id === connectionId);

  return {
    connections,
    connectionId,
    setConnectionId,
    connection,
    searchParams: params,
  };
}

/** Bootstrap q / asin / named filters from URL + GFE search once. */
export function useCommerceQueryBootstrap(opts: {
  onQuery?: (q: string) => void;
  statusParam?: string;
  allowedStatuses?: readonly string[];
  onStatus?: (status: string) => void;
}) {
  const [params] = useSearchParams();
  const gfe = useGlobalFilterOptional();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || params.get('asin') || params.get('gfe_q') || gfe?.context.search || '';
    if (uq) opts.onQuery?.(uq);
    const key = opts.statusParam || 'status';
    const st = params.get(key) || params.get('gfe_ostatus') || '';
    if (st && opts.allowedStatuses?.includes(st)) {
      opts.onStatus?.(st);
    }
    setBootstrapped(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot bootstrap
  }, [params, bootstrapped]);

  return { bootstrapped, searchParams: params };
}
